import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  Hidden,
} from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useDispatch, useSelector } from 'react-redux';
import { BarChart, GTranslate, KeyboardArrowUp } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import Logo from '@images/logo-with-text.png';
import { useAbility } from '@casl/react';
import {
  makeSelectUser,
  selectViewRoleId,
  selectHasGlobalDashboard,
  selectHasNotificationModule,
} from '../../managers/AuthManager/selectors';
import {
  performLogoutAction,
  updateViewRoleAct,
} from '../../managers/AuthManager/actions';
import { resetLogin } from '../../managers/LoginManager/actions';
import { DynamicHeaderTitle } from './DynamicHeaderTitle';
import { stringAvatar } from '../../utils/common.util';

import { appLocales } from '../../i18n';

import messages from './messages';
import commonMessages from '../common/messages';
import { makeSelectLocale } from '../../managers/LanguageProviderManager/selectors';
import { changeLocale } from '../../managers/LanguageProviderManager/actions';
import NotificationListManager from '../../managers/NotificationListManager';
import NotificationDropdown from './NotificationDropdown';
import { AbilityContext } from '../../casl/AbilityContext';
import RoleSelectorDialog from './modals/RoleSelectorDialog';
import useDialog from '../../hooks/useDialog';

const DEFAULT_ROUTE = process.env.REACT_APP_LANDING_URL || '/events/active';

export function Appbar() {
  const intl = useIntl();
  const isProduction = process.env.REACT_APP_IS_PROD_BUILD === 'true';

  // translation text
  const {
    profileText,
    logoutText,
    manageUserTxt,
    confirmationTxt,
    logoutWarnTxt,
    cancelTxt,
    logoutTxt,
    switchRoleToAdminTxt,
    switchRoleToOnboarderTxt,
    globalDashboardTxt,
  } = useMemo(
    () => ({
      profileText: intl.formatMessage(messages.profile),
      logoutText: intl.formatMessage(messages.logout),
      manageUserTxt: intl.formatMessage(messages.manageUser),
      confirmationTxt: intl.formatMessage(commonMessages.confirmation),
      logoutWarnTxt: intl.formatMessage(messages.logoutWarn),
      cancelTxt: intl.formatMessage(commonMessages.cancel),
      logoutTxt: intl.formatMessage(messages.logout),
      switchRoleToAdminTxt: intl.formatMessage(messages.switchRoleToAdmin),
      switchRoleToOnboarderTxt: intl.formatMessage(
        messages.switchRoleToOnboarder,
      ),
      globalDashboardTxt: intl.formatMessage(messages.globalDashboard),
    }),
    [intl],
  );

  const currentUser = useSelector(makeSelectUser());
  const currentViewRole = useSelector(selectViewRoleId);
  const currentLocale = useSelector(makeSelectLocale());
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const canViewNotification =
    useSelector(selectHasNotificationModule) &&
    ability.can('read', 'Notification');
  const canViewGlobalDashboard =
    useSelector(selectHasGlobalDashboard) &&
    ability.can('read', 'AnnualDashboard');
  const canViewUsers = ability.can('read', 'User');
  let canSwitchRole = false;
  if (currentUser && currentUser.role) {
    canSwitchRole = currentUser.role.roleName === 'GlobalAdmin';
  }
  const avatarProp = stringAvatar(currentUser.displayName);

  const handleLogoutConfirmOpen = () => {
    handleCloseUserMenu();
    setIsLogoutConfirmOpen(true);
  };

  const handleViewRoleChange = useCallback(
    newRole => {
      handleCloseUserMenu();
      const { id: roleId } = newRole;
      const isActualRole = roleId === currentUser.role.id;
      dispatch(updateViewRoleAct(isActualRole ? 2 : 1, newRole));
    },
    [currentUser.role.id, dispatch],
  );

  const {
    open: openRoleChooser,
    openDialog: openRoleChooserDialog,
    closeDialog: closeRoleChooserDialog,
  } = useDialog();

  const onViewRoleChange = () => {
    handleCloseUserMenu();
    openRoleChooserDialog();
  };

  const handleLogoutConfirmClose = () => setIsLogoutConfirmOpen(false);

  const handleOpenUserMenu = event => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenLanguageMenu = event => {
    setAnchorElLanguage(event.currentTarget);
  };

  const handleCloseLanguageMenu = () => {
    setAnchorElLanguage(null);
  };

  const handleLanguageChange = key => {
    dispatch(changeLocale(key));
  };

  const handleLogout = () => {
    handleLogoutConfirmClose();
    dispatch(resetLogin());
    dispatch(performLogoutAction());
  };

  return (
    <AppBar
      position="sticky"
      color="transparent"
      sx={{
        backgroundColor: '#FFFFFF',
        zIndex: theme => theme.zIndex.drawer + 1,
        top: isProduction ? 0 : 49,
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: '100vw' }}>
        <Toolbar disableGutters sx={{ minHeight: { md: '84px' } }}>
          <Link component={RouterLink} to={DEFAULT_ROUTE}>
            <Box
              component="img"
              src={Logo}
              alt=""
              sx={{
                display: { md: 'flex' },
                mr: { xs: 1, md: 2, lg: 5 },
                width: {
                  xs: '64px',
                  sm: '90px',
                  md: '154px',
                },
                py: 2,
                pr: 2,
              }}
            />
          </Link>
          <Box flexGrow={1} display="flex" alignItems="center">
            <DynamicHeaderTitle></DynamicHeaderTitle>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {canViewNotification && (
              <NotificationListManager>
                <NotificationDropdown />
              </NotificationListManager>
            )}
            {canViewGlobalDashboard && (
              <Hidden mdDown>
                <Tooltip title={globalDashboardTxt} arrow>
                  <IconButton
                    component={RouterLink}
                    to="/annual-stats"
                    color="primary"
                  >
                    <BarChart />
                  </IconButton>
                </Tooltip>
              </Hidden>
            )}
            <Hidden mdDown>
              <IconButton
                sx={{ p: 0, mr: 1 }}
                aria-label="Change language"
                color="inherit"
                onClick={handleOpenLanguageMenu}
              >
                <GTranslate />
              </IconButton>
            </Hidden>
            <IconButton sx={{ p: 0, mr: 1 }}>
              <Avatar sx={avatarProp.sx}>{avatarProp.children}</Avatar>
            </IconButton>
            <Hidden mdDown>
              <Typography component="div">
                <Box sx={{ fontWeight: 'bold' }}>{currentUser.firstName}</Box>
              </Typography>
            </Hidden>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                {anchorElUser ? <KeyboardArrowDownIcon /> : <KeyboardArrowUp />}
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={handleCloseUserMenu}>
                <Link
                  component={RouterLink}
                  underline="none"
                  to={`/users/${currentUser.id}`}
                  sx={{ flexGrow: 1 }}
                >
                  <Typography>{profileText}</Typography>
                </Link>
              </MenuItem>
              {canViewUsers && (
                <MenuItem onClick={handleCloseUserMenu}>
                  <Link
                    component={RouterLink}
                    underline="none"
                    to="/admin"
                    sx={{ flexGrow: 1 }}
                  >
                    <Typography>{manageUserTxt}</Typography>
                  </Link>
                </MenuItem>
              )}
              {canSwitchRole && (
                <MenuItem onClick={onViewRoleChange}>
                  <Typography textAlign="center">
                    {currentViewRole !== currentUser?.role?.id
                      ? switchRoleToAdminTxt
                      : switchRoleToOnboarderTxt}
                  </Typography>
                </MenuItem>
              )}
              <MenuItem onClick={handleLogoutConfirmOpen}>
                <Typography textAlign="center">{logoutText}</Typography>
              </MenuItem>
            </Menu>
            <Menu
              sx={{ mt: '45px' }}
              id="language-appbar"
              anchorEl={anchorElLanguage}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElLanguage)}
              onClose={handleCloseLanguageMenu}
            >
              {appLocales.map(key => (
                <MenuItem
                  key={key}
                  onClick={() => handleLanguageChange(key)}
                  selected={key === currentLocale}
                >
                  <Typography
                    sx={{ textTransform: 'uppercase' }}
                    textAlign="center"
                  >
                    {key}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
      <Dialog
        open={isLogoutConfirmOpen}
        onClose={handleLogoutConfirmClose}
        aria-labelledby="logout-confirm-dialog-title"
        aria-describedby="logout-confirm-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="logout-confirm-dialog-title">
          {confirmationTxt}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-confirm-dialog-description">
            {logoutWarnTxt}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogoutConfirmClose}
          >
            {cancelTxt}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleLogout}
            autoFocus
            color="primary"
          >
            {logoutTxt}
          </Button>
        </DialogActions>
      </Dialog>
      {openRoleChooser && (
        <RoleSelectorDialog
          onClose={closeRoleChooserDialog}
          onRoleChange={handleViewRoleChange}
        />
      )}
    </AppBar>
  );
}
